import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingUI from "../components/FloatingUI";
import { Reveal, SectionTag } from "../components/motion";
import { IMG, wa, PASSEIOS_DETALHE } from "../lib/site";

const PASSEIOS = PASSEIOS_DETALHE.map((p) => ({
  flag: p.flag,
  title: p.title,
  desc: p.cardDesc,
  img: p.img,
  cta: p.cta,
  slug: p.slug,
}));

export default function Passeios() {
  useEffect(() => {
    document.title = "Passeios e Experiências em Foz do Iguaçu | Magnus Máximo";
    window.scrollTo(0, 0);
    return () => {
      document.title = "Magnus Máximo | Guia de Turismo em Foz do Iguaçu — Cataratas, Argentina e Paraguai";
    };
  }, []);

  return (
    <>
      <Header />
      <main>
        <section className="relative overflow-hidden bg-brand-deep grain pt-52 pb-24 md:pb-32" data-testid="passeios-hero">
          <div className="absolute inset-0">
            <img src={IMG.hero} alt="Cataratas do Iguaçu" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-deep/70 to-brand-deep" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <Link
                to="/"
                data-testid="passeios-voltar"
                className="inline-flex items-center gap-2 text-white/70 text-sm font-semibold transition-colors hover:text-accent-soft"
              >
                <ArrowLeft size={15} />
                Voltar ao início
              </Link>
              <h1 className="mt-6 max-w-3xl font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-balance">
                Passeios e Experiências em <span className="font-serif italic font-medium text-accent-soft">Foz do Iguaçu</span>
              </h1>
              <p className="mt-6 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
                Descubra as principais atrações de Foz do Iguaçu e região com passeios incríveis para aproveitar cada momento da sua viagem. Conheça paisagens naturais, faça compras e explore os principais destinos do Brasil, Argentina e Paraguai.
              </p>
              <a
                href={wa("Olá, Magnus! Quero consultar passeios e disponibilidade em Foz do Iguaçu.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="passeios-hero-cta"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-whatsapp px-8 py-4 text-sm font-bold text-white shadow-xl shadow-whatsapp/30 transition-transform duration-300 hover:scale-[1.04]"
              >
                <MessageCircle size={18} />
                Consultar passeios e disponibilidade
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-sand" data-testid="passeios-lista">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal className="mb-16">
              <SectionTag>Tríplice Fronteira</SectionTag>
              <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-brand-deep text-balance">
                Nossos <span className="font-serif italic font-medium text-brand-light">principais passeios</span>
              </h2>
            </Reveal>

            <div className="space-y-8">
              {PASSEIOS.map((p, i) => (
                <Reveal key={p.title} delay={0.05}>
                  <article
                    data-testid={`passeio-detalhe-${i}`}
                    className={`group grid md:grid-cols-2 rounded-3xl overflow-hidden border border-brand/10 bg-white ${i % 2 ? "" : ""}`}
                  >
                    <div className={`relative min-h-[260px] md:min-h-[340px] overflow-hidden ${i % 2 ? "md:order-2" : ""}`}>
                      <img
                        src={p.img}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-col justify-center p-8 md:p-12">
                      <span className="text-brand-light text-xs font-bold uppercase tracking-[0.3em]">{p.flag} Passeio {String(i + 1).padStart(2, "0")}</span>
                      <h3 className="mt-3 font-display font-extrabold text-brand-deep text-2xl md:text-3xl text-balance">{p.title}</h3>
                      <p className="mt-4 text-moss text-sm md:text-base leading-relaxed">{p.desc}</p>
                      <div className="mt-7 flex flex-wrap items-center gap-4">
                        <Link
                          to={`/passeios/${p.slug}`}
                          data-testid={`passeio-detalhe-link-${i}`}
                          className="group/btn inline-flex w-fit items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.04] shadow-lg shadow-brand/25"
                        >
                          Ver detalhes do passeio
                          <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Link>
                        <a
                          href={wa(`Olá, Magnus! ${p.cta}. Pode me passar mais detalhes e disponibilidade?`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`passeio-cta-${i}`}
                          className="inline-flex items-center gap-2 text-sm font-bold text-brand transition-colors duration-300 hover:text-accent"
                        >
                          <MessageCircle size={16} />
                          Chamar no WhatsApp
                        </a>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-brand-deep grain py-24 md:py-32" data-testid="passeios-cta-final">
          <div className="absolute inset-0">
            <img src={IMG.macuco} alt="Macuco Safari nas Cataratas do Iguaçu" className="w-full h-full object-cover opacity-25" loading="lazy" />
            <div className="absolute inset-0 bg-brand-deep/70" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="max-w-2xl font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-balance">
                Viva essa <span className="font-serif italic font-medium text-accent-soft">experiência</span>
              </h2>
              <p className="mt-6 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
                Planeje seus passeios e aproveite o melhor de Foz do Iguaçu, Argentina e Paraguai. Tenha conforto, praticidade e uma experiência inesquecível durante sua viagem.
              </p>
              <p className="mt-4 max-w-xl text-white/70 text-sm md:text-base">
                Entre em contato e consulte nossos passeios e disponibilidade.
              </p>
              <a
                href={wa("Olá, Magnus! Quero viver essa experiência. Pode consultar passeios e disponibilidade para mim?")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="passeios-final-cta"
                className="mt-9 inline-flex items-center gap-3 rounded-full bg-whatsapp px-10 py-5 text-base font-bold text-white shadow-2xl shadow-whatsapp/40 transition-transform duration-300 hover:scale-[1.05]"
              >
                <MessageCircle size={20} />
                Falar com o Magnus no WhatsApp
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingUI />
    </>
  );
}
